import { useServerFn } from "@tanstack/react-start";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GrowthForm } from "@/components/admin/growth/GrowthForm";
import { GrowthResultsPanel } from "@/components/admin/growth/GrowthResultsPanel";
import {
  EMPTY_INPUTS,
  SETTING_DEFAULTS,
  calculate,
  planFilename,
  type GrowthInputs,
  type Scenario,
  type SettingsMap,
} from "@/lib/growth-plan";
import { getGrowthPlan, getGrowthSettings, getPlanContent, saveGrowthPlan } from "@/lib/growth-plan.functions";

const STORAGE_KEY = "kcn-growth-plan-draft";

export const Route = createFileRoute("/_authenticated/admin/growth-plan/")({
  validateSearch: (s: Record<string, unknown>) => ({ id: typeof s.id === "string" ? s.id : undefined }),
  component: GrowthPlanTool,
});

function GrowthPlanTool() {
  const { id } = useSearch({ from: "/_authenticated/admin/growth-plan/" });
  const settingsFn = useServerFn(getGrowthSettings);
  const contentFn = useServerFn(getPlanContent);
  const saveFn = useServerFn(saveGrowthPlan);
  const loadFn = useServerFn(getGrowthPlan);

  const [inputs, setInputs] = useState<GrowthInputs>(EMPTY_INPUTS);
  const [settings, setSettings] = useState<SettingsMap>(SETTING_DEFAULTS);
  const [content, setContent] = useState<any[]>([]);
  const [recordId, setRecordId] = useState<string | null>(null);
  const [user, setUser] = useState<{ name: string; email: string }>({ name: "Kristan Cole Network", email: "" });
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hydrated = useRef(false);

  // Load settings, plan content, and the signed-in user.
  useEffect(() => {
    (async () => {
      try {
        const rows = await settingsFn({});
        const map: SettingsMap = { ...SETTING_DEFAULTS };
        for (const r of rows as any[]) map[r.key] = Number(r.value);
        setSettings(map);
      } catch { /* keep defaults */ }
      try {
        setContent((await contentFn({})) as any[]);
      } catch { /* ignore */ }
      const { data } = await supabase.auth.getUser();
      if (data.user) {
        setUser({
          name: (data.user.user_metadata?.full_name as string) || data.user.email?.split("@")[0] || "KCN",
          email: data.user.email ?? "",
        });
      }
    })();
  }, [settingsFn, contentFn]);

  // Hydrate from a saved record; otherwise always start blank.
  useEffect(() => {
    (async () => {
      if (id) {
        try {
          const row: any = await loadFn({ data: { id } });
          if (row) {
            setInputs({ ...EMPTY_INPUTS, ...(row.inputs || {}) });
            setRecordId(row.id);
          }
        } catch { /* ignore */ }
      } else {
        setInputs(EMPTY_INPUTS);
        setRecordId(null);
        setStatus(null);
        try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      }
      hydrated.current = true;
    })();
  }, [id, loadFn]);

  const patch = useCallback((p: Partial<GrowthInputs>) => setInputs((prev) => ({ ...prev, ...p })), []);
  const results = useMemo(() => calculate(inputs, settings), [inputs, settings]);

  async function onDownload() {
    setBusy(true);
    setStatus(null);
    try {
      const [{ pdf }, { GrowthPlanPdf }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("@/components/admin/growth/GrowthPlanPdf"),
      ]);
      const blob = await pdf(
        <GrowthPlanPdf
          inputs={inputs}
          results={results}
          content={content as any}
          preparedBy={user.name}
          preparedByEmail={user.email}
        />,
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = planFilename(inputs.agentName);
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setStatus(e.message ?? "PDF failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSave() {
    if (!inputs.agentName.trim()) return setStatus("Add the agent's name before saving.");
    setBusy(true);
    setStatus(null);
    try {
      const res: any = await saveFn({
        data: {
          id: recordId,
          agent_name: inputs.agentName,
          agent_email: inputs.agentEmail || null,
          agent_phone: inputs.agentPhone || null,
          current_brokerage: inputs.brokerage || null,
          market: inputs.market || null,
          track: inputs.track,
          inputs,
          results,
          net_delta: Math.round(results.sameProductionDelta),
          total_delta: Math.round(results.totalDelta),
          prepared_by_name: user.name,
        },
      });
      if (res?.id) setRecordId(res.id);
      setStatus("Saved to records.");
    } catch (e: any) {
      setStatus(e.message ?? "Save failed");
    } finally {
      setBusy(false);
    }
  }

  function onReset() {
    if (!confirm("Clear the form and start a new plan?")) return;
    setInputs(EMPTY_INPUTS);
    setRecordId(null);
    setStatus(null);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }

  return (
    <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div>
        <GrowthForm value={inputs} onChange={patch} />
      </div>
      <div className="lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-2">
        <GrowthResultsPanel
          inputs={inputs}
          results={results}
          onScenario={(s: Scenario) => patch({ scenario: s })}
          onCompounding={(b) => patch({ compounding: b })}
        />
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={onDownload} disabled={busy} className="bg-primary px-5 py-3 text-xs uppercase tracking-[0.18em] text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            Generate Growth Plan PDF
          </button>
          <button onClick={onSave} disabled={busy} className="border border-border px-5 py-3 text-xs uppercase tracking-[0.18em] text-foreground hover:border-primary disabled:opacity-50">
            Save to Records
          </button>
          <button onClick={onReset} className="px-5 py-3 text-xs uppercase tracking-[0.18em] text-foreground/60 hover:text-foreground">
            Start New
          </button>
        </div>
        {status ? <p className="mt-3 text-sm text-primary">{status}</p> : null}
      </div>
    </div>
  );
}
